import {
  AS400_EXPORT_GROUP_LOCATION_COLUMN,
  as400BranchSplitLocationsForDivision,
  as400DivisionCode,
  as400DivisionExportsPerBranch,
  as400StockingBranchFor,
  rdDcForItemMasterLetter,
} from '@erp/rpc';
import type { As400ExportGroup } from '@erp/rpc';
import { AS400_EXPORT_GROUP_ORDER } from './as400Groups';
import type { DivisionOption } from './useDivisionSelection';

/**
 * The divisions the Add-row dialog starts with checked: the one in view when
 * the grid shows exactly one (the row is almost certainly for it), otherwise
 * none — with several in view, which of them carry the product is the
 * reviewer's call.
 */
export function initialNewRowDivisionIds(
  inViewIds: readonly string[],
): string[] {
  return inViewIds.length === 1 ? [...inViewIds] : [];
}

/**
 * The worksheets an Add-row pass authors rows on, in tab order — so a row
 * added to several lands on each in the order the workbook lists them.
 */
export function chosenNewRowGroups(
  checked: readonly As400ExportGroup[],
): As400ExportGroup[] {
  return AS400_EXPORT_GROUP_ORDER.filter((group) => checked.includes(group));
}

/**
 * The divisions an Add-row pass authors rows for, in division order.
 *
 * Offered from every catalog division, not from the ones in view: the view
 * leaves out divisions the review has nothing under (they are greyed out and
 * unselectable), and listing a product into such a division — a division that
 * doesn't stock it yet — is exactly what adding a row is for (GEN-2719). An org
 * with a single division has nothing to choose, so the row belongs to it.
 */
export function chosenNewRowDivisions(
  dataSources: readonly DivisionOption[],
  checkedIds: readonly string[],
): DivisionOption[] {
  if (dataSources.length === 1) return [...dataSources];
  const checked = new Set(checkedIds);
  return dataSources.filter((dataSource) => checked.has(dataSource.id));
}

/** One row an Add-row pass authors: a division, and the location it goes to. */
export interface NewRowTarget {
  dataSourceId: string;
  /** The location code; null = the whole division (every branch). */
  branch: string | null;
  /** The CDB key the row shows under: division code, then its location. */
  cdb: string;
}

/**
 * Where a chosen division's rows go: one row for the division, or (RD) one per
 * location picked — `picked` null = untouched = all of `available`.
 */
export type NewRowDivisionLocations =
  | { kind: 'division' }
  | {
      kind: 'locations';
      available: readonly string[];
      picked: readonly string[] | null;
    };

/** The pick in list order (all until narrowed), minus codes not offered. */
export function pickedNewRowLocations(
  available: readonly string[],
  picked: readonly string[] | null,
): string[] {
  if (picked === null) return [...available];
  const set = new Set(picked);
  return available.filter((code) => set.has(code));
}

/** A location as the checklist shows it: on RD, the code with its DC's name. */
export function newRowLocationLabel(
  divisionTitle: string,
  code: string,
): string {
  if (as400DivisionCode(divisionTitle) !== 'RD') return code;
  const dc = rdDcForItemMasterLetter(code)?.dc;
  return dc ? `${code} · ${dc}` : code;
}

/**
 * The rows one Add-row pass authors: one per division, or for RD one per
 * location picked, each under its own CDB (`RDA`, `RDC`, …). `complete` is
 * false with nothing to add or an RD division with no location ticked.
 */
export function newRowTargets(
  divisions: readonly { dataSourceId: string; code: string }[],
  locationsOf: (dataSourceId: string) => NewRowDivisionLocations,
): { targets: NewRowTarget[]; complete: boolean } {
  const targets: NewRowTarget[] = [];
  let complete = divisions.length > 0;
  for (const { dataSourceId, code } of divisions) {
    const locations = locationsOf(dataSourceId);
    if (locations.kind === 'division') {
      targets.push({ dataSourceId, branch: null, cdb: code });
      continue;
    }
    const picked = pickedNewRowLocations(locations.available, locations.picked);
    if (picked.length === 0) complete = false;
    for (const branch of picked) {
      targets.push({ dataSourceId, branch, cdb: `${code}${branch}` });
    }
  }
  return { targets, complete: complete && targets.length > 0 };
}

/**
 * What a new row starts with in its location cell, on a worksheet that has one
 * (Stock, Stock Add): the stocking location of the row's branch where that is
 * known — RD's DC cross-reference, so a row added at RD's A starts at K. Empty
 * for a division-wide row, or one whose stocking location isn't known: the
 * reviewer types it.
 */
export function newRowLocationSeed(
  group: As400ExportGroup,
  target: Pick<NewRowTarget, 'branch' | 'cdb'>,
): Record<string, string> {
  const column = AS400_EXPORT_GROUP_LOCATION_COLUMN[group];
  if (column === undefined || target.branch === null) return {};
  const division = target.cdb.slice(0, -target.branch.length);
  const location = as400StockingBranchFor(division, target.branch);
  return location === null ? {} : { [column]: location };
}

/**
 * The row a CDB typed into a new row's CDB cell is for: the division whose
 * code it starts with, then, on a division whose upload runs per branch (RD),
 * one of its locations (`RDA`). Null when it names no division, or a location
 * the division doesn't add rows at — RD needs one, every other division adds
 * a row for the whole division and takes none. The longest code wins, so a
 * division code that prefixes another's never shadows it.
 */
export function parseNewRowCdb(
  dataSources: readonly DivisionOption[],
  typed: string | null,
): NewRowTarget | null {
  const cdb = typed?.trim().toUpperCase() ?? '';
  const matches: { dataSource: DivisionOption; code: string }[] = [];
  for (const dataSource of dataSources) {
    const code = as400DivisionCode(dataSource.title);
    if (code !== '' && cdb.startsWith(code)) matches.push({ dataSource, code });
  }
  matches.sort((a, b) => b.code.length - a.code.length);
  for (const { dataSource, code } of matches) {
    const location = cdb.slice(code.length);
    if (!as400DivisionExportsPerBranch(dataSource.title)) {
      if (location === '')
        return { dataSourceId: dataSource.id, branch: null, cdb };
      continue;
    }
    if (
      new Set(as400BranchSplitLocationsForDivision(dataSource.title)).has(
        location,
      )
    ) {
      return { dataSourceId: dataSource.id, branch: location, cdb };
    }
  }
  return null;
}

/**
 * A new row's cells as it saves under `to`, the target its CDB names. Its
 * location cell follows the CDB: where the cell still holds what it was seeded
 * with under `from` (or is empty), it takes `to`'s stocking location — so a
 * row added at RDA and retyped to RDB doesn't go out in B's file with A's
 * location. A location the reviewer typed is theirs and stays.
 */
export function newRowValuesAt(
  group: As400ExportGroup,
  values: Record<string, string | null>,
  from: Pick<NewRowTarget, 'branch' | 'cdb'> | null,
  to: Pick<NewRowTarget, 'branch' | 'cdb'>,
): Record<string, string | null> {
  const column = AS400_EXPORT_GROUP_LOCATION_COLUMN[group];
  if (column === undefined) return values;
  const current = values[column] ?? '';
  const seeded = from ? (newRowLocationSeed(group, from)[column] ?? '') : '';
  if (current !== '' && current !== seeded) return values;
  const next = newRowLocationSeed(group, to)[column];
  if (next === current) return values;
  if (next !== undefined) return { ...values, [column]: next };
  // A stale seed with nothing known to replace it: the reviewer types it.
  return current === '' ? values : { ...values, [column]: null };
}
